<script setup lang="ts">
import type { DynamicDialogInstance } from 'primevue/dynamicdialogoptions'

const dialog = inject<DynamicDialogInstance>('dialogRef')
const isMobile = inject<Ref<boolean>>('isMobile')

const dialogRef = ref(dialog)

const headerImage = computed(() => dialogRef.value?.data.headerImage)
const onCloseReturnUndefined = computed(() => !!dialogRef.value?.data?.onCloseUndefined)

const hasBothButtons = computed(
  () => dialogRef.value?.data.btnPrimary && dialogRef.value?.data.btnSecondary
)

const primaryButtonClass = computed(() => {
  if (hasBothButtons.value) return dialogRef.value?.data.btnPrimaryClass || ''
  return `${dialogRef.value?.data.btnPrimaryClass || ''} w-full md:w-fit`
})

// only hide close on mobile
const isCloseHidden = computed(() => isMobile?.value && dialogRef.value?.data.isCloseHidden)

const handleClose = () => {
  const value = onCloseReturnUndefined.value ? undefined : false
  dialogRef.value?.close(value)
}

const handleActions = (type: string) => {
  if (type === 'secondary') return dialogRef.value?.close(false)
  return dialogRef.value?.close(true)
}
</script>

<template>
  <!-- header -->
  <Teleport v-if="isMobile && !isCloseHidden" to="body">
    <div class="fixed left-4 top-8 z-[1101] md:hidden">
      <CloseIcon fill="white" width="24" height="24" @click="handleClose" />
    </div>
  </Teleport>
  <div class="relative mt-8 h-fit p-0">
    <CloseIcon
      v-if="!isMobile && !isCloseHidden"
      :fill="'black'"
      width="16"
      height="16"
      @click="handleClose"
    />

    <img
      v-if="headerImage"
      :src="headerImage"
      alt="alert image"
      class="absolute bottom-0 left-1/2 h-auto w-40 -translate-x-1/2 transform md:left-auto md:right-0 md:transform-none"
    />
  </div>

  <!-- body -->
  <div
    data-test="alert-dialog__body"
    class="flex flex-col gap-4 px-4 py-8 md:px-0"
    :class="{ 'pt-0 md:pt-8': !headerImage }"
  >
    <p class="text-center text-lg font-bold tracking-[-0.15px] md:text-left md:text-2xl">
      {{ dialog?.data.title }}
    </p>

    <p
      v-dompurify-html="dialog?.data.body"
      class="text-center text-sm leading-4 tracking-[-0.15px] md:text-left"
    ></p>
  </div>

  <!-- actions -->
  <div
    data-test="alert-dialog__actions"
    class="flex flex-row items-center gap-0 md:justify-end md:gap-4 md:pb-8"
    :class="dialogRef?.data.verticalActions ? 'md:flex-col-reverse md:items-end' : ''"
  >
    <McdButton
      v-if="dialog?.data.btnSecondary"
      class="h-16 w-1/2 rounded-none border-0 border-t py-3 text-sm leading-4 md:h-fit md:w-fit md:rounded md:border md:px-4"
      is-secondary
      @click="handleActions('secondary')"
    >
      {{ dialog.data.btnSecondary }}
    </McdButton>

    <McdButton
      v-if="dialog?.data.btnPrimary"
      class="h-16 w-1/2 rounded-none border-0 border-t py-3 text-sm leading-4 md:h-fit md:w-fit md:rounded md:border md:px-4"
      :class="primaryButtonClass"
      is-primary
      @click="handleActions('primary')"
    >
      {{ dialog.data.btnPrimary }}
    </McdButton>
  </div>
</template>

<style>
div[data-pc-section='mask'] {
  @apply bg-black/50 backdrop-blur;
}
</style>
